import { CircularProgress } from "@material-ui/core";
import { Duration, LocalDate, ZoneId, ZonedDateTime } from "@js-joda/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { SalaryNotice } from "model/Salary";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isAdjustableMonth } from "web-apps/company/utils/timeAndDate";
import { getDateLocale } from "web-apps/company/utils/utils";
import { isCurrentOrPastMonth } from "../salaryCutOff.logic";
import {
  SalaryPageBottomMargin,
  SalaryNoticeActions,
  LoadMoreSentinel,
  LoadMoreSpinner,
  PeriodNavigatorContainer,
  SalaryPeriodCard,
  TooltipContainer,
} from "../salary.styled";
import { FileViewer } from "../../fileViewer/fileViewer.component";
import { ModalState } from "../individualSalary.component";
import { AdjustSalaryModal } from "../adjustSalaryModal.component";
import { getSalaryAdjustment } from "../adjustSalary.logic";
import {
  useMonthlySalary,
  type MonthlySalaryLoadedState,
} from "./monthlySalary.hook";
import { MonthlySalaryRow } from "./monthlySalaryRow.component";
import { EmptyStateText, SalaryList } from "./monthlySalary.styled";

interface AdjustModalState {
  initialValue?: number;
  open: boolean;
  salaryNoticeId?: number;
  workerId?: number;
  workerName?: string;
}

interface MonthlySalaryProps {
  currentDate: LocalDate;
  onOpenGenerateSalaryNoticesModal: () => void;
  refreshKey: number;
  setCurrentDate: (date: LocalDate) => void;
}

const systemZoneId = ZoneId.systemDefault();

const toJsDate = (date: LocalDate): Date =>
  new Date(date.year(), date.monthValue() - 1, date.dayOfMonth());

const assertNever = (value: never): never => {
  throw new Error(`Unhandled monthly salary state: ${JSON.stringify(value)}`);
};

export const MonthlySalary = (props: MonthlySalaryProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long" }),
    [locale],
  );
  const [{ company, companyWorkerImages, workerIdToExternalId }] =
    useCompanyStore();
  const { fetchState, refreshWorkerNotice, loadMore } = useMonthlySalary(
    company?.id,
    props.currentDate,
    props.refreshKey,
  );
  const { loadMoreRef } = useInfiniteScroll({
    hasMore: fetchState.status === "loaded" && fetchState.hasMore,
    isLoading:
      fetchState.status === "loading" ||
      (fetchState.status === "loaded" && fetchState.isLoadingMore),
    itemCount:
      fetchState.status === "loaded" ? fetchState.salaryNotices.length : 0,
    onLoadMore: loadMore,
  });
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });
  const [adjustModalState, setAdjustModalState] = useState<AdjustModalState>({
    open: false,
  });
  const [today, setToday] = useState<LocalDate>(() =>
    LocalDate.now(systemZoneId),
  );

  useEffect(() => {
    const now = ZonedDateTime.now(systemZoneId);
    const nextMidnight = now
      .toLocalDate()
      .plusDays(1)
      .atStartOfDay(systemZoneId);
    const timeoutId = window.setTimeout(
      () => setToday(LocalDate.now(systemZoneId)),
      Duration.between(now, nextMidnight).toMillis(),
    );

    return () => window.clearTimeout(timeoutId);
  }, [today]);

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      workerId: undefined,
      pdfId: undefined,
      period: undefined,
      workerName: undefined,
    });
  }, []);

  const openAdjustModal = useCallback((salary: SalaryNotice) => {
    setAdjustModalState({
      open: true,
      initialValue: getSalaryAdjustment(salary),
      salaryNoticeId: salary.id,
      workerId: salary.workerId,
      workerName: salary.workerName,
    });
  }, []);

  const closeAdjustModal = useCallback(() => {
    setAdjustModalState({ open: false });
  }, []);

  const handleAdjustSaved = useCallback(() => {
    const adjustedWorkerId = adjustModalState.workerId;
    closeAdjustModal();
    // Only the adjusted worker's notice can have changed.
    if (adjustedWorkerId != null) refreshWorkerNotice(adjustedWorkerId);
  }, [adjustModalState.workerId, closeAdjustModal, refreshWorkerNotice]);

  const { open, workerId, pdfId, period, workerName } = modalState;

  const canAdjustForDisplayedMonth = isAdjustableMonth(props.currentDate);
  const canNavigateToNextMonth = isCurrentOrPastMonth({
    month: props.currentDate.plusMonths(1),
    today,
  });

  const handleMonthChange = (nextDate: LocalDate): void => {
    if (!isCurrentOrPastMonth({ month: nextDate, today })) return;
    props.setCurrentDate(nextDate);
  };

  const renderSalaryRows = (salaryNotices: SalaryNotice[]): JSX.Element[] => {
    return salaryNotices.map((salary) => {
      const currency = salary.currency === "SEK" ? " kr" : "";
      const externalId = workerIdToExternalId[salary.workerId];

      return (
        <MonthlySalaryRow
          key={`monthlysalary-${salary.id}`}
          salary={salary}
          avatarUrl={companyWorkerImages[externalId]}
          currency={currency}
          canAdjustForDisplayedMonth={canAdjustForDisplayedMonth}
          onOpenPdf={openSalaryPdf}
          onOpenAdjust={openAdjustModal}
        />
      );
    });
  };

  const renderLoadingState = (): JSX.Element => (
    <EmptyStateText>
      <CircularProgress />
    </EmptyStateText>
  );

  const renderErrorState = (): JSX.Element => (
    <EmptyStateText>
      <Text typography="b2" align="center" color={ColorV2.NegativeRed}>
        {t("Salary.MonthlySalaryLoadError")}
      </Text>
    </EmptyStateText>
  );

  const renderEmptyState = (): JSX.Element => (
    <EmptyStateText>
      <Text typography="b2" align="center">
        {t("Salary.GenerateSalaryNoticesEmptyState", {
          month: monthFormatter.format(
            toJsDate(props.currentDate.withDayOfMonth(1)),
          ),
        })}
      </Text>
      <Text typography="b2" align="center">
        {t("Salary.GenerateSalaryNoticesEmptyStateDescription")}
      </Text>
      <Text typography="b2" align="center">
        {t("Salary.GenerateSalaryNoticesEmptyStateAvailability")}
      </Text>
    </EmptyStateText>
  );

  const renderLoadedState = (state: MonthlySalaryLoadedState): JSX.Element => {
    if (state.salaryNotices.length === 0) {
      return renderEmptyState();
    }

    return (
      <>
        <SalaryList>{renderSalaryRows(state.salaryNotices)}</SalaryList>
        {state.isLoadingMore && (
          <LoadMoreSpinner>
            <CircularProgress size={24} />
          </LoadMoreSpinner>
        )}
        {state.hasMore && <LoadMoreSentinel ref={loadMoreRef} />}
      </>
    );
  };

  const renderSalaryContent = (): JSX.Element => {
    switch (fetchState.status) {
      case "idle":
      case "loading":
        return renderLoadingState();
      case "error":
        return renderErrorState();
      case "loaded":
        return renderLoadedState(fetchState);
      default:
        return assertNever(fetchState);
    }
  };

  return (
    <SalaryPageBottomMargin>
      <SalaryPeriodCard>
        <PeriodNavigatorContainer>
          <PeriodNavigator
            currentDate={props.currentDate}
            mode="month"
            onChange={handleMonthChange}
            allowNext={canNavigateToNextMonth}
          />
        </PeriodNavigatorContainer>
      </SalaryPeriodCard>

      <SalaryNoticeActions>
        <TooltipContainer>
          <TooltipHover text={t("Salary.MonthlySalarySlipInfo")} arrow="up">
            <Text typography="s1">{t("Salary.MonthlySalaryInfoTitle")}</Text>
          </TooltipHover>
        </TooltipContainer>
        <PrimaryButton
          label={t("Salary.GenerateSalaryNotices")}
          iconName="v2/plus"
          onClick={props.onOpenGenerateSalaryNoticesModal}
          size="small"
        />
      </SalaryNoticeActions>

      {renderSalaryContent()}

      {open && company && workerId && pdfId && period && workerName && (
        <Modal
          isOpen={open}
          onClose={closeModal}
          label={t("Salary.ViewSalaryNoticeTooltip")}
          size="large"
        >
          <FileViewer
            fetchEndpoint={() =>
              Api().company.salary.generateSalaryNotice(
                company.id,
                workerId,
                pdfId,
              )
            }
            filename={`SalaryNotice_${period}_${workerName}.pdf`}
            type="application/pdf"
          />
        </Modal>
      )}

      {company &&
        adjustModalState.open &&
        adjustModalState.salaryNoticeId &&
        adjustModalState.workerId &&
        adjustModalState.workerName &&
        adjustModalState.initialValue !== undefined && (
          <AdjustSalaryModal
            open={adjustModalState.open}
            companyId={company.id}
            initialValue={adjustModalState.initialValue}
            salaryNoticeId={adjustModalState.salaryNoticeId}
            workerId={adjustModalState.workerId}
            workerName={adjustModalState.workerName}
            onClose={closeAdjustModal}
            onSaved={handleAdjustSaved}
          />
        )}
    </SalaryPageBottomMargin>
  );
};
